/**
 * Client boundary for special-event registration approval (CHI-6).
 *
 * Constants and row types come from `@shared/registrationApproval`. The admin
 * queue and registrant status call the real `events.*` procedures.
 */
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import {
  EVENT_FULL,
  EVENT_FULL_TRPC_CODE,
  PENDING_QUEUE_DEFAULT_LIMIT,
  REGISTRATION_WAITLISTED,
  SCHEMA_NOT_READY,
  STALE_VERSION,
  STALE_VERSION_TRPC_CODE,
  type AdminTransitionInput,
  type PendingQueueStatus,
  type PendingRegistrationStatus,
} from "@shared/registrationApproval";

export {
  ADMIN_DECLINE_REASON,
  EVENT_FULL,
  EVENT_FULL_MESSAGE,
  EVENT_FULL_TRPC_CODE,
  PENDING_QUEUE_DEFAULT_LIMIT,
  PENDING_QUEUE_MAX_LIMIT,
  REGISTRATION_WAITLISTED,
  SCHEMA_NOT_READY,
  STALE_VERSION,
  STALE_VERSION_TRPC_CODE,
} from "@shared/registrationApproval";

export type {
  AdminRegistrationAction,
  AdminTransitionInput,
  AdminTransitionResult,
  ApproveResult,
  DeclineResult,
  ListPendingResult,
  PendingQueueEventSummary,
  PendingQueueStatus,
  PendingRegistration,
  PendingRegistrationStatus,
  ReopenResult,
} from "@shared/registrationApproval";

export type ListPendingInput = {
  eventId?: string;
  search?: string;
  status?: PendingQueueStatus;
  limit?: number;
  cursor?: string | null;
};

export type MyRegistrationStatus = {
  status: PendingRegistrationStatus;
  /** Always `null` until the registration is `registered`. */
  joinUrl: string | null;
};

function errorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const code = (error as { data?: { code?: unknown } | null }).data?.code;
  return typeof code === "string" ? code : undefined;
}

/** The server's error formatter copies the machine-readable reason to `error.data.reason`. */
function errorReason(error: unknown): string | undefined {
  const reason = (error as { data?: { reason?: unknown } | null } | null)?.data?.reason;
  return typeof reason === "string" ? reason : undefined;
}

export type RegistrationErrorKind = "event_full" | "stale" | "not_found" | "not_allowed" | "not_ready" | "other";

/** Event full is checked first, so an EVENT_FULL reason always wins over a stale classification. */
export function classifyRegistrationError(error: unknown): RegistrationErrorKind {
  const code = errorCode(error);
  const reason = errorReason(error);
  if (code === EVENT_FULL_TRPC_CODE && reason === EVENT_FULL) return "event_full";
  if (code === STALE_VERSION_TRPC_CODE && reason === STALE_VERSION) return "stale";
  if (code === "NOT_FOUND") return "not_found";
  if (code === "BAD_REQUEST") return "not_allowed";
  if (code === "PRECONDITION_FAILED" && reason === SCHEMA_NOT_READY) return "not_ready";
  return "other";
}

export const REGISTRANT_BLOCK_MESSAGES = {
  pending: "Your registration is awaiting approval.",
  waitlisted: "You’re on the waitlist for this event.",
} as const;

/**
 * Special (approval-queue) events own the disabled waitlist CTA. The public
 * pages already carry this on the event object (`isSpecialEvent`, or
 * `specialEventApproval.isSpecialEvent` when that envelope is present).
 * Ordinary events — including legacy waitlisted rows — self-register as on main.
 */
export function isApprovalQueueEvent(
  event: { isSpecialEvent?: boolean; specialEventApproval?: { isSpecialEvent?: boolean } } | null | undefined,
): boolean {
  return event?.isSpecialEvent === true || event?.specialEventApproval?.isSpecialEvent === true;
}

/**
 * When `events.register` is refused because the caller is waitlisted on a
 * special event, the registrant-facing status and message; otherwise `null`.
 * A repeat register while pending succeeds as `pending_review` instead.
 */
export function registrationBlockFromError(error: unknown): { status: "waitlisted"; message: string } | null {
  if (errorCode(error) !== "PRECONDITION_FAILED") return null;
  if (errorReason(error) === REGISTRATION_WAITLISTED) return { status: "waitlisted", message: REGISTRANT_BLOCK_MESSAGES.waitlisted };
  return null;
}

export const REGISTER_FAILURE_FALLBACK = "We could not complete your registration. Please try again.";

/**
 * After any failed `events.register` — a TRPC error or an `unavailable` result —
 * refetch `myRegistrations` so a stale page can flip the CTA. A waitlisted
 * refusal keeps its info toast; everything else keeps the generic or server
 * message.
 */
export function presentRegisterFailure(
  error: unknown,
  refetchMyRegistrations: () => void,
): { kind: "blocked"; message: string } | { kind: "failed"; message: string } {
  refetchMyRegistrations();
  const block = registrationBlockFromError(error);
  if (block) return { kind: "blocked", message: block.message };
  return {
    kind: "failed",
    message: error instanceof Error && error.message ? error.message : REGISTER_FAILURE_FALLBACK,
  };
}

/** Kinds where the row on screen is out of date and the queue should be refetched. */
export function shouldRefreshAfter(kind: RegistrationErrorKind) {
  return kind === "stale" || kind === "not_found";
}

export function describeRegistrationError(error: unknown): string {
  switch (classifyRegistrationError(error)) {
    case "event_full":
      return "This event is full. No seats remain, so the registration was not changed.";
    case "stale":
      return "This registration changed since you loaded it. The queue has been refreshed.";
    case "not_found":
      return "This registration no longer exists. The queue has been refreshed.";
    case "not_allowed":
      return "That change isn’t allowed from this registration’s current status.";
    case "not_ready":
      return "Registration approvals aren’t available yet. No changes were made.";
    default:
      return error instanceof Error && error.message && errorCode(error) !== "CONFLICT"
        ? error.message
        : "The registration could not be updated. Please try again.";
  }
}

const LIST_PENDING_KEY = ["events.listPending"] as const;

type ListPendingFilters = Pick<ListPendingInput, "eventId" | "search" | "status">;

/** `events.listPending`: server-paged, oldest `submittedAt` first. Changing a filter starts again from page one. */
export function useEventsListPending(input: ListPendingFilters) {
  const utils = trpc.useUtils();
  const filters: ListPendingFilters = {
    ...(input.eventId ? { eventId: input.eventId } : {}),
    ...(input.search?.trim() ? { search: input.search.trim() } : {}),
    ...(input.status && input.status !== "pending" ? { status: input.status } : {}),
  };
  return useInfiniteQuery({
    queryKey: [...LIST_PENDING_KEY, filters],
    queryFn: ({ pageParam }) =>
      utils.events.listPending.fetch({ ...filters, limit: PENDING_QUEUE_DEFAULT_LIMIT, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: lastPage => (lastPage.hasMore ? lastPage.nextCursor : null),
    /* Dropping an inactive filter's pages means returning to it starts again at page one. */
    gcTime: 0,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Refetch the queue, e.g. after a STALE_VERSION conflict. */
export function useRefreshEventsListPending() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: LIST_PENDING_KEY });
}

function useRegistrationTransition(
  useProcedure: (options: { onSuccess: () => void }) => { mutateAsync: (input: AdminTransitionInput) => Promise<{ ok: boolean }> },
) {
  const queryClient = useQueryClient();
  return useProcedure({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_PENDING_KEY }),
  });
}

export const useEventsApprove = () => useRegistrationTransition(trpc.events.approve.useMutation);
export const useEventsReopen = () => useRegistrationTransition(trpc.events.reopen.useMutation);
export const useEventsDecline = () => useRegistrationTransition(trpc.events.decline.useMutation);

/** Existing DB registration states that belong to the approval flow. */
const APPROVAL_STATE_BY_DB_STATE: Record<string, PendingRegistrationStatus> = {
  pending_review: "pending",
  waitlisted: "waitlisted",
  registered: "registered",
};

/** Maps a `myRegistrations` row's `registrationState` to the approval status, if it is part of that flow. */
export function approvalStatusFromRegistrationState(registrationState: string | undefined): PendingRegistrationStatus | undefined {
  return registrationState ? APPROVAL_STATE_BY_DB_STATE[registrationState] : undefined;
}

/**
 * The signed-in registrant's approval status for one event, or `null` when they
 * have no registration in the approval flow.
 */
export function useMyRegistrationStatus(eventSlug: string, options: { enabled: boolean }) {
  const registrationsQuery = trpc.events.myRegistrations.useQuery(undefined, { enabled: options.enabled, retry: false });
  const dbState = registrationsQuery.data?.find(registration => registration.eventSlug === eventSlug)?.registrationState;
  const status = approvalStatusFromRegistrationState(dbState);
  /* The link is never requested before the registration is approved. */
  const joinLinkQuery = trpc.events.myZoomJoinLink.useQuery(
    { eventSlug },
    { enabled: options.enabled && status === "registered", retry: false },
  );
  const data: MyRegistrationStatus | null = status
    ? { status, joinUrl: status === "registered" ? joinLinkQuery.data?.joinUrl ?? null : null }
    : null;
  return { data, isLoading: Boolean(registrationsQuery.isLoading) };
}
